import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "types/store";
import moment from "moment";
import { toast } from "react-toastify";
import { Button, ButtonGroup } from "aible";
import chatAppButtonIcon from "images/browser-regular.svg";
import documentButtonIcon from "images/files-regular.svg";
import dataButtonIcon from "images/table-regular.svg";
import CSVIconPurple2 from "images/file-csv-light-purple.svg";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import styles from "./style.module.css";
import { CHATAIBLE_BLUEPRINTS, CHATAIBLE_DOCUMENT_SUMMARIZATION, CHATAIBLE_SQL } from "containers/ChatGPT/reducer";
import SenseDataHeader from "components/Tiles/Sense/SenseDataHeader";
import UploadDocument from "components/LanguageModelArtifact/UploadDocument";
import { HiddenFilePicker } from "components/View/UploadModal";
import ConnectDataWarehouseButton from "components/Tiles/Sense/ConnectDataWarehouseButton";
import SchemaMonitorContainer from "components/SchemaMonitor/SchemaMonitor";
import SearchInput from "components/Inputs/SearchInput/SearchInput";
import * as nextReducer from "containers/Next/NextReducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as viewReducer from "containers/View/reducer";
import * as languageModelArtifactReducer from "containers/LanguageModelArtifact/reducer";
import * as schemaMonitorReducer from "containers/SchemaMonitor/reducer";
import * as homeReducer from "containers/Home/reducer";
import { isContentMimeType } from "@/types/chat";
import { isEnabled } from "@/utils/featureFlags";
import { clsx } from "clsx";
export const HiddenRawFilePicker = ({ innerRef, onUpdate, multiple }) => {
    const handleChange = (event) => {
        if (event.currentTarget === null || !("files" in event.currentTarget)) {
            return;
        }
        const files = event.currentTarget?.files ? event.currentTarget.files : undefined;
        onUpdate(files);
    };
    return (React.createElement("div", { style: { display: "none" } },
        React.createElement("input", { type: "file", id: "fUpload", ref: innerRef, onChange: (e) => {
                handleChange(e);
            }, multiple: multiple })));
};
export const AttachContextButton = ({ onComplete, multiple, ...buttonProps }) => {
    const dispatch = useDispatch();
    const FileUploadRef = useRef(null);
    const blueprintId = useSelector((state) => chatGPTReducer.getBlueprintId(state));
    const selectBlueprint = (val) => dispatch(chatGPTReducer.selectBlueprint(val));
    return (React.createElement("div", { style: { display: "flex" } },
        React.createElement(HiddenRawFilePicker, { multiple: multiple, innerRef: FileUploadRef, onUpdate: (files) => {
                if (files === undefined) {
                    dispatch(chatGPTReducer.updatePromptAttachments(undefined));
                    return;
                }
                dispatch(chatGPTReducer.updatePromptAttachments(undefined));
                let allFileNames = "";
                let filesRead = 0;
                for (const file of files) {
                    allFileNames += (allFileNames === "" ? "" : ", ") + file?.name;
                    const fileReader = new FileReader();
                    fileReader.addEventListener("load", () => {
                        filesRead += 1;
                        const result = fileReader.result?.toString();
                        if (result !== undefined) {
                            const parts = result.split(";");
                            let mimeType = parts[0].substring(5);
                            if (mimeType.startsWith("text/") || mimeType.startsWith("application/xml")) {
                                mimeType = "text/plain";
                            }
                            if (isContentMimeType(mimeType)) {
                                dispatch(chatGPTReducer.addPromptAttachments({
                                    [file.name]: {
                                        type: "data",
                                        mime_type: mimeType,
                                        filename: file.name,
                                        data: parts[1].substring(7)
                                    }
                                }));
                                if (blueprintId === CHATAIBLE_BLUEPRINTS.CHATAIBLE_DATA_SEARCH.id) {
                                    selectBlueprint("CHATAIBLE_RAW_CHAT");
                                }
                            }
                            else {
                                toast(`Unsupported File Type: ${mimeType}`, {
                                    type: toast.TYPE.ERROR,
                                    position: toast.POSITION.TOP_CENTER,
                                    autoClose: 3500
                                });
                            }
                        }
                        if (filesRead === files.length) {
                            return onComplete !== undefined ? onComplete(allFileNames) : Promise.resolve();
                        }
                    });
                    fileReader.readAsDataURL(file);
                }
            } }),
        React.createElement(Button, { ...buttonProps, onClick: () => FileUploadRef?.current?.click() })));
};
const ContextSelectionPanel = ({ nextId, isClose, forceChatAppInit, allowAgents }) => {
    const dispatch = useDispatch();
    const [isUploading, updateIsUploading] = useState(false);
    const [searchDataText, updateSearchDataText] = useState();
    const currentNextId = useSelector((state) => nextReducer.getCurrentNextId(state));
    const retNextId = nextId ?? currentNextId ?? "new";
    const modelExternalId = useSelector(chatGPTReducer.getModelExternalId);
    const datasetId = useSelector((state) => nextReducer.getDatasetId(state, retNextId));
    const artifactId = useSelector((state) => chatGPTReducer.getArtifactId(state));
    const contextSelectionPanelTab = useSelector((state) => chatGPTReducer.getContextSelectionPanelTab(state));
    const updateContextSelectionPanelTab = (val) => dispatch(chatGPTReducer.updateContextSelectionPanelTab(val));
    const newChatKPIPanel = useSelector((state) => chatGPTReducer.getNewChatKPIPanel(state));
    const updateNewChatKPIPanel = (val) => dispatch(chatGPTReducer.updateNewChatKPIPanel(val));
    const datasets = useSelector((state) => viewReducer.getDatasets(state));
    const artifacts = useSelector((state) => languageModelArtifactReducer.getCorpusList(state));
    const chatApps = useSelector((state) => viewReducer.getChatApps(state, true, "STANDARD_AND_AZ"));
    const schemaMonitors = useSelector((state) => schemaMonitorReducer.getSchemaMonitors(state));
    const selectBlueprint = (val) => dispatch(chatGPTReducer.selectBlueprint(val));
    const updateDatasetId = (val) => dispatch(nextReducer.updateDatasetId(retNextId, val));
    const updateArtifactId = (val) => dispatch(chatGPTReducer.updateArtifactId(val));
    const updateIsUploadDocument = (val) => dispatch(chatGPTReducer.updateIsUploadDocument(val));
    const initChatApp = (chatApp) => dispatch(chatGPTReducer.initChatApp(chatApp));
    const initChatAppHelper = (chatAppId) => dispatch(chatGPTReducer.initChatAppHelper(chatAppId));
    const resetSuggestedColumns = () => dispatch(nextReducer.updateSuggestedColumns(retNextId, undefined));
    const resetDataLeakageMessage = () => dispatch(nextReducer.updateDataLeakageMessage(retNextId, undefined));
    const resetSimpleFlowStep = () => dispatch(nextReducer.updateSimpleFlowStep(retNextId, undefined));
    const resetSimpleFlowStepDisplay = () => dispatch(nextReducer.updateSimpleFlowStepDisplay(retNextId, undefined));
    const handleReset = () => {
        resetSuggestedColumns();
        resetDataLeakageMessage();
        resetSimpleFlowStep();
        resetSimpleFlowStepDisplay();
    };
    const blueprintId = useSelector((state) => chatGPTReducer.getBlueprintId(state));
    const shouldUpdateUsecase = isNaN(Number(blueprintId));
    const chatType = useSelector((state) => chatGPTReducer.getChatType(state));
    const CsvUploadRef = useRef(null);
    const FileUploadRef = useRef(null);
    const retDatasets = datasets !== undefined
        ? datasets.filter((d) => !searchDataText ||
            d?.label === undefined ||
            (searchDataText !== undefined &&
                d?.label !== undefined &&
                d?.label.toLowerCase().indexOf(searchDataText.toLowerCase()) !== -1))
        : [];
    const retArtifacts = artifacts !== undefined
        ? artifacts
            .filter((a) => !searchDataText ||
            a?.name === undefined ||
            (searchDataText !== undefined &&
                a?.name !== undefined &&
                a?.name.toLowerCase().indexOf(searchDataText.toLowerCase()) !== -1))
            .filter((a) => Boolean(a?.metadata?.document_list?.length))
        : [];
    const retChatApps = chatApps !== undefined
        ? chatApps
            .filter((c) => !searchDataText ||
            c?.name === undefined ||
            (searchDataText !== undefined &&
                c?.name !== undefined &&
                c?.name.toLowerCase().indexOf(searchDataText.toLowerCase()) !== -1))
            .reverse()
        : [];
    return (React.createElement("div", { className: styles.selectDataBoxHolder },
        React.createElement("div", { className: clsx(isEnabled("spark-with-partners") && styles.rightDataHeader), style: { paddingLeft: "7px" } },
            "What context do you want to add?",
            isClose && (React.createElement(Button, { onClick: () => updateContextSelectionPanelTab(undefined), type: "secondary", style: { marginLeft: "auto", fontWeight: "normal" } }, "Close"))),
        React.createElement("div", { className: styles.rightDataUpload },
            React.createElement("div", { className: styles.rightDataUploadButtons },
                React.createElement("div", { style: { display: "flex" } },
                    React.createElement(HiddenFilePicker, { innerRef: CsvUploadRef, onStart: () => {
                            updateArtifactId(undefined);
                            updateDatasetId(undefined);
                            updateContextSelectionPanelTab("DATA");
                            updateIsUploading(true);
                            handleReset();
                        }, onComplete: (uDataset) => {
                            if (uDataset?.id) {
                                updateDatasetId(uDataset.id);
                                if (shouldUpdateUsecase) {
                                    selectBlueprint(CHATAIBLE_SQL.id);
                                }
                            }
                            updateIsUploading(false);
                            // updateContextSelectionPanelTab(undefined);
                        } }),
                    blueprintId !== "CHATAIBLE_DOCUMENT_SUMMARIZATION" && !isEnabled("spark-with-partners") && (React.createElement(Button, { type: "secondary", style: { marginRight: "5px" }, onClick: () => CsvUploadRef?.current?.click(), isLoading: isUploading, loadingText: "Uploading..." },
                        "Upload CSV",
                        React.createElement("div", null,
                            React.createElement("div", null,
                                React.createElement("img", { src: CSVIconPurple2 })))))),
                React.createElement(UploadDocument, { isCreate: true, isSmall: false, onStart: () => {
                        updateArtifactId(undefined);
                        updateDatasetId(undefined);
                        updateContextSelectionPanelTab("DOCUMENT");
                    }, onCreate: (artifact) => {
                        if (artifact?.id) {
                            if (shouldUpdateUsecase) {
                                selectBlueprint(CHATAIBLE_DOCUMENT_SUMMARIZATION.id);
                            }
                            updateArtifactId(artifact.id);
                            updateIsUploadDocument(false);
                            handleReset();
                            // updateContextSelectionPanelTab(undefined);
                        }
                    }, onComplete: (artifact) => {
                        console.log(artifact);
                    }, skipWaitForReindex: true }),
                !isEnabled("spark-with-partners") && (React.createElement(AttachContextButton, { type: "secondary", style: { marginRight: "12px" } },
                    "Attach File",
                    React.createElement("div", null,
                        React.createElement("div", null,
                            React.createElement("img", { src: CSVIconPurple2 })))))),
            !isEnabled("spark-with-partners") && (React.createElement("div", null,
                React.createElement(SchemaMonitorContainer, null),
                React.createElement(ConnectDataWarehouseButton, null)))),
        React.createElement("div", { className: styles.selectDataBox },
            React.createElement("div", { className: styles.selectDataHeaderActions },
                React.createElement("div", null,
                    React.createElement(ButtonGroup, null,
                        !isEnabled("spark-with-partners") && (React.createElement(Button, { onClick: () => {
                                // Disabling usecase changes on tab switching, happens when you select a dataset/document
                                // if (shouldUpdateUsecase) {
                                //   selectBlueprint(CHATAIBLE_SQL.id);
                                // }
                                // updateArtifactId(undefined);
                                updateContextSelectionPanelTab("DATA");
                            }, active: contextSelectionPanelTab === "DATA" },
                            React.createElement("div", { className: styles.tabButtonIcon },
                                React.createElement("img", { src: dataButtonIcon })),
                            "Data")),
                        React.createElement(Button, { onClick: () => {
                                // Disabling usecase changes on tab switching, happens when you select a dataset/document
                                // if (shouldUpdateUsecase) {
                                //   selectBlueprint(CHATAIBLE_DOCUMENT_SUMMARIZATION.id);
                                // }
                                // updateDatasetId(undefined);
                                updateContextSelectionPanelTab("DOCUMENT");
                            }, active: contextSelectionPanelTab === "DOCUMENT" },
                            React.createElement("div", { className: styles.tabButtonIcon },
                                React.createElement("img", { src: documentButtonIcon })),
                            isEnabled("spark-with-partners") ? "Previously Uploaded Documents" : "Documents"),
                        !isEnabled("spark-with-partners") && (React.createElement(Button, { onClick: () => {
                                updateDatasetId(undefined);
                                updateArtifactId(undefined);
                                updateContextSelectionPanelTab("CHAT_APP");
                            }, active: contextSelectionPanelTab === "CHAT_APP", disabled: !allowAgents },
                            React.createElement("div", { className: allowAgents ? styles.tabButtonIcon : styles.tabButtonIcon + " " + styles.tabButtonIconDisabled },
                                React.createElement("img", { src: chatAppButtonIcon })),
                            "Agents")))),
                contextSelectionPanelTab !== "ADD" && (React.createElement("div", { className: styles.selectDataSearch },
                    React.createElement(SearchInput, { style: { fontSize: "13px" }, inputClass: styles.selectDataSearchInput, onChange: (val) => {
                            updateSearchDataText(val);
                        } })))),
            React.createElement("div", { className: styles.selectDataContent }, contextSelectionPanelTab === "ADD" ? (React.createElement(SenseDataHeader, { isOnlyAddData: true, isSmall: true })) : contextSelectionPanelTab === "DATA" && retDatasets ? (retDatasets.length === 0 ? (React.createElement("div", { className: styles.selectNoDataFound }, "No Datasets Found")) : (retDatasets.map((d) => {
                // const outcomeOptions = d?.fields
                //   ? Object.keys(d?.fields)
                //       .filter(
                //         (f) =>
                //           (f !== d?.outcome) &&
                //           ((d.fields[f]?.data && d.fields[f]?.data.length === 2) ||
                //           d.fields[f].type === "number")
                //       )
                //       .map((f) => d.fields[f])
                //   : [];
                const isLoading = d?.status_name !== "DONE" && d?.status_name !== "ERROR";
                const sourceDisplay = homeReducer.getDatasetSourceDisplay(d, schemaMonitors);
                return (React.createElement("div", { className: styles.selectDataItem +
                        " " +
                        (d?.id === datasetId ? styles.isActive : "") +
                        " " +
                        (isLoading ? styles.isLoading : ""), key: d?.id, style: {
                        cursor: "default"
                    } },
                    React.createElement("div", null,
                        React.createElement("div", { className: styles.selectDataLabel, style: {
                                display: "flex",
                                alignItems: "center"
                            } },
                            React.createElement("div", { className: styles.selectDataIcon },
                                React.createElement("img", { src: sourceDisplay?.iconGray, alt: sourceDisplay?.label })),
                            React.createElement("div", { className: styles.selectDataText }, d?.label)),
                        React.createElement("div", { className: styles.selectDataDate }, `${sourceDisplay.label} | LAST UPDATE: ` + moment.utc(d?.created).local().fromNow()),
                        React.createElement("div", { className: styles.selectActions },
                            React.createElement(Button, { style: { marginRight: "10px" }, onClick: () => {
                                    if (chatType !== "SQL") {
                                        selectBlueprint(CHATAIBLE_SQL.id);
                                    }
                                    updateArtifactId(undefined);
                                    updateDatasetId(d?.id);
                                    handleReset();
                                    updateNewChatKPIPanel(false);
                                    dispatch(nextReducer.updateIsKPISelectionOpen(retNextId, false));
                                    updateContextSelectionPanelTab(undefined);
                                }, disabled: isLoading }, "Ask Anything (NLQ)"),
                            React.createElement("div", { className: styles.selectDataDate }, `Insights about`),
                            d?.outcome !== undefined && (React.createElement(Button, { style: { marginLeft: "10px" }, onClick: () => {
                                    if (chatType !== "AIBLE") {
                                        selectBlueprint(CHATAIBLE_BLUEPRINTS.CHATAIBLE_ANALYTICS.id);
                                    }
                                    updateArtifactId(undefined);
                                    updateDatasetId(d?.id);
                                    handleReset();
                                    updateNewChatKPIPanel(false);
                                    dispatch(nextReducer.updateIsKPISelectionOpen(retNextId, false));
                                    updateContextSelectionPanelTab(undefined);
                                }, disabled: isLoading },
                                React.createElement("div", { style: { overflow: "hidden", textOverflow: "ellipsis", maxWidth: "260px" } }, d?.outcome))),
                            React.createElement(Button, { style: { marginLeft: "10px" }, onClick: () => {
                                    if (chatType !== "SQL" && chatType !== "AIBLE") {
                                        selectBlueprint(CHATAIBLE_SQL.id);
                                    }
                                    updateArtifactId(undefined);
                                    updateDatasetId(d?.id);
                                    handleReset();
                                    dispatch(nextReducer.updateCurrentNextId("new"));
                                    dispatch(nextReducer.updateSimpleFlowStep(retNextId, "BEGIN"));
                                    dispatch(nextReducer.updateIsKPISelectionOpen(retNextId, true));
                                    updateNewChatKPIPanel(true);
                                }, disabled: isLoading }, "Choose KPI"))),
                    isLoading && (React.createElement(LoadingIndicator, { style: {
                            margin: "0px",
                            marginLeft: "auto",
                            transform: "scale(0.5)"
                        } }))));
            }))) : contextSelectionPanelTab === "DOCUMENT" && retArtifacts ? (retArtifacts.length === 0 ? (React.createElement("div", { className: styles.selectNoDataFound }, "No Documents Found")) : (retArtifacts.map((a, i) => {
                const isLoading = a?.metadata?.status !== "COMPLETE" && a?.metadata?.status !== "ERROR";
                return (React.createElement("div", { className: styles.selectDataItem +
                        " " +
                        (a?.id === artifactId ? styles.isActive : "") +
                        " " +
                        (isLoading ? styles.isLoading : ""), key: a?.id, onClick: () => {
                        if (shouldUpdateUsecase || chatType !== "DOCUMENT") {
                            selectBlueprint(CHATAIBLE_DOCUMENT_SUMMARIZATION.id);
                        }
                        updateDatasetId(undefined);
                        updateArtifactId(a?.id);
                        handleReset();
                        updateContextSelectionPanelTab(undefined);
                    } },
                    React.createElement("div", null,
                        React.createElement("div", { className: styles.selectDataLabel }, a?.name),
                        React.createElement("div", { className: styles.selectDataOutcome }, (a?.metadata?.document_list ? a?.metadata?.document_list.length : "0") + " total documents")),
                    isLoading && (React.createElement(LoadingIndicator, { style: {
                            margin: "0px",
                            marginLeft: "auto",
                            transform: "scale(0.5)"
                        } }))));
            }))) : contextSelectionPanelTab === "CHAT_APP" && retChatApps ? (retChatApps.length === 0 ? (React.createElement("div", { className: styles.selectNoDataFound }, "No Chat Apps Found")) : (retChatApps.map((c, i) => {
                const retDescription = c?.wizard?.description;
                return (React.createElement("div", { className: styles.selectDataItem, key: c?.id, onClick: () => {
                        updateDatasetId(undefined);
                        updateArtifactId(undefined);
                        handleReset();
                        updateContextSelectionPanelTab(undefined);
                        initChatApp(c);
                        if (forceChatAppInit) {
                            // This is needed to init the app if we're already in a project
                            initChatAppHelper(c?.id);
                        }
                    } },
                    React.createElement("div", null,
                        React.createElement("div", { className: styles.selectDataLabel }, c?.name),
                        retDescription ? React.createElement("div", { className: styles.selectDataOutcome }, retDescription) : null,
                        React.createElement("div", { className: styles.selectDataDate }, c?.created_by?.fullname))));
            }))) : (React.createElement("div", null))))));
};
export default ContextSelectionPanel;
